import { addDays, type LocalDate } from '../dates';
import type { Mutation } from '../mutations';
import { daysBetween } from '../spending/range';
import type { CategoryLike } from '../spending/categories';
import type { BuildCtx } from '../spending/types';
import { allDues, isOpen } from './project';
import { LOAN_PAYMENT_KEY } from './rules';
import { paymentsMutation, type Due, type PaymentsSnapshot } from './types';
import { seededId } from '../ids';

export const DUE_WINDOW_DAYS = 30;

export type DueFlag = 'late' | 'today' | 'soon' | 'upcoming';

export interface ListedDue extends Due {
  flag: DueFlag;
  /** Negative when late. */
  daysAway: number;
  /**
   * Auto-debit (a bill, utility, fixed loan or card statement) on or after its due date with
   * nothing recorded: "Did X auto-pay ₱Y?"
   */
  askAutoPay: boolean;
}

function listed(d: Due, today: LocalDate, remindDays: number): ListedDue {
  const daysAway = daysBetween(today, d.dueDate);
  const flag: DueFlag =
    daysAway < 0 ? 'late' : daysAway === 0 ? 'today' : daysAway <= remindDays ? 'soon' : 'upcoming';
  return {
    ...d,
    flag,
    daysAway,
    askAutoPay: d.autoPay && daysAway <= 0 && d.payments.length === 0,
  };
}

/**
 * "Payments due · next 30 days" (spec §5): every open or partly paid due up to `days` ahead, late
 * ones (any age, since the payee was added) first. Card statements with nothing owed are left out.
 */
export function dueList(
  snap: PaymentsSnapshot,
  today: LocalDate,
  days: number = DUE_WINDOW_DAYS,
): ListedDue[] {
  const until = addDays(today, days);
  const remind = new Map(snap.payees.map((p) => [p.id, p.remindDays]));
  return allDues(snap, until, today)
    .filter((d) => isOpen(d) && d.remainingMinor > 0 && d.dueDate <= until)
    .map((d) => listed(d, today, remind.get(d.payeeId) ?? 3));
}

/** Today's questions: auto-debits whose day has come (OPEN-QUESTIONS v0.5 Q3). */
export const autoPayAsks = (list: readonly ListedDue[]) => list.filter((d) => d.askAutoPay);

/** The total of the dues in the next 7 days (the due-this-week tile), late ones included. */
export function dueThisWeek(list: readonly ListedDue[]): { count: number; totalMinor: number } {
  const week = list.filter((d) => d.daysAway <= 7);
  return { count: week.length, totalMinor: week.reduce((s, d) => s + d.remainingMinor, 0) };
}

/** Spending dropped Loan payment from its defaults in v0.1.1; Payments creates it when turned on. */
export const LOAN_PAYMENT_CATEGORY = { key: LOAN_PAYMENT_KEY, name: 'Loan payment', icon: 'bank' };

/**
 * Makes sure Loan payment exists on seededId(u, 'category', 'loan_payment'): an insert-if-absent
 * seed when missing; a plain empty insert restores a soft-deleted one and keeps its name (a rename
 * survives, conventions §14); a live one is left alone.
 */
export function buildEnsureLoanPaymentCategory(
  userId: string,
  existing: readonly CategoryLike[],
  ctx: BuildCtx,
): Mutation[] {
  const id = seededId(userId, 'category', LOAN_PAYMENT_CATEGORY.key);
  const row = existing.find((c) => c.id === id);
  if (row && row.deletedAt === null) return [];
  if (row) return [paymentsMutation(ctx, 'categories', 'insert', id, {})];
  const position =
    existing.filter((c) => c.deletedAt === null).reduce((max, c) => Math.max(max, c.position), 0) +
    1;
  return [
    paymentsMutation(
      ctx,
      'categories',
      'insert',
      id,
      {
        key: LOAN_PAYMENT_CATEGORY.key,
        name: LOAN_PAYMENT_CATEGORY.name,
        icon: LOAN_PAYMENT_CATEGORY.icon,
        hidden: false,
        position,
      },
      { ifAbsent: true },
    ),
  ];
}
